import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'viewer' });

  const load = async () => { const { data } = await api.get('/users'); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post('/users', form); toast.success(t('success')); setOpen(false); setForm({ email: '', name: '', password: '', role: 'viewer' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };
  const updateRole = async (u, role) => { await api.patch(`/users/${u.id}`, { role }); load(); };
  const toggleActive = async (u) => { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); };
  const remove = async (id) => { if (!window.confirm('Confirm?')) return; await api.delete(`/users/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('users_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{items.length} users</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="users-create-button"><Plus className="h-4 w-4 mr-2" />{t('users_new')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('users_new')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></div>
              <div className="space-y-1"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" /></div>
              <div className="space-y-1"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t('role_admin')}</SelectItem>
                    <SelectItem value="tech">{t('role_tech')}</SelectItem>
                    <SelectItem value="viewer">{t('role_viewer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button><Button onClick={create} data-testid="user-form-submit">{t('create')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border border-border">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Active</th><th className="px-4 py-3 text-right"></th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                    <SelectTrigger className="w-[130px] h-8" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t('role_admin')}</SelectItem>
                      <SelectItem value="tech">{t('role_tech')}</SelectItem>
                      <SelectItem value="viewer">{t('role_viewer')}</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3"><Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.id}`} /></td>
                <td className="px-4 py-3 text-right">
                  {u.id !== user?.id && <Button variant="ghost" size="sm" onClick={() => remove(u.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
